import { useDisplay } from "vuetify";

/**
 * The single source of truth for "is the phone shell active".
 *
 * ⚠️ WHY THIS IS NOT `configStore.isLessThanOverlayNavBreakpoint`. That flag (1279px) drives the
 * desktop sidebar, which is NOT a `VNavigationDrawer` but a hand-rolled `<aside>` translated
 * off-screen by a HARDCODED `@media (max-width: 1279px)` in `VerticalNav.vue`. Its JS value and its
 * CSS live in four disconnected places (`themeConfig.ts`, `@layouts/config.ts`, `VerticalNav.vue`,
 * and a `d-lg-none` class) that agree only by coincidence. Reusing or changing it would desync JS
 * state from the CSS transform. This gate is deliberately ADDITIVE and CSS-independent: the
 * existing sidebar keeps working untouched, and the phone shell layers on top.
 *
 * Three bands result, which is intentional:
 *   < 960px    → phone shell (bottom tabs + More sheet, no hamburger)
 *   960–1279px → existing overlay drawer + hamburger, unchanged
 *   ≥ 1280px   → fixed sidebar, unchanged
 *
 * `smAndDown` is the app-wide convention (used by 32 files incl. every existing mobile component),
 * so reusing it keeps the shell consistent with `MobileFilterList`, `PetProfileMobileTabs` and the
 * rest. That consistency is worth more than a phone-exact cutoff.
 */
export function useMobileShell() {
  const { smAndDown } = useDisplay();

  /**
   * Kill switch. If the phone shell ever regresses in production it can be disabled without
   * reverting the branch; every consumer reads `isMobileShell`, never `smAndDown` directly.
   */
  const isMobileShellEnabled = ref(true);

  const isMobileShell = computed(() => isMobileShellEnabled.value && smAndDown.value);

  return {
    /** True when the phone shell should render. Use this, not `smAndDown`, for shell decisions. */
    isMobileShell,
    isMobileShellEnabled,
    /** Raw breakpoint, for content that adapts without the shell being involved. */
    smAndDown,
  };
}
